import { z } from "zod"

import {
  correlationIdSchema,
  entityIdSchema,
  platformApprovalIdSchema,
  platformAuditEventIdSchema,
  platformCommandIdSchema,
  platformContextIdSchema,
  platformOperatorIdSchema,
  platformOutboxEventIdSchema,
  platformSessionIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const timestampSchema = z.string().datetime({ offset: true })
const safeRecordSchema = z.record(z.string(), z.json())
const boundedRecord = (maximumBytes: number) =>
  safeRecordSchema.refine(
    (value) => new TextEncoder().encode(JSON.stringify(value)).byteLength
      <= maximumBytes,
    `Safe JSON record exceeds ${maximumBytes} bytes.`,
  )

export const platformCommandTargetV1Schema = z.discriminatedUnion("scope", [
  z
    .object({
      scope: z.literal("platform"),
      targetTenantId: z.null(),
      targetType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
      targetId: entityIdSchema,
      expectedVersion: z.number().int().nonnegative().safe(),
    })
    .strict(),
  z
    .object({
      scope: z.literal("tenant"),
      targetTenantId: tenantIdSchema,
      targetType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
      targetId: entityIdSchema,
      expectedVersion: z.number().int().nonnegative().safe(),
    })
    .strict(),
])

export const platformCommandEnvelopeV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformCommandId: platformCommandIdSchema,
    platformContextId: platformContextIdSchema,
    platformOperatorId: platformOperatorIdSchema,
    platformSessionId: platformSessionIdSchema,
    correlationId: correlationIdSchema,
    idempotencyNamespace: z
      .string()
      .regex(/^[a-z][a-z0-9_.-]{2,95}$/),
    idempotencyKey: z
      .string()
      .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/),
    issuedAt: timestampSchema,
    recentAuthenticationAt: timestampSchema,
    target: platformCommandTargetV1Schema,
    requiredApprovalIds: z.array(platformApprovalIdSchema).max(8),
    commandType: z.string().regex(/^[A-Z][A-Za-z0-9]{2,95}$/),
    commandVersion: z.literal(1),
    payload: boundedRecord(16_384),
  })
  .strict()
  .superRefine((envelope, context) => {
    if (new Set(envelope.requiredApprovalIds).size
      !== envelope.requiredApprovalIds.length) {
      context.addIssue({
        code: "custom",
        path: ["requiredApprovalIds"],
        message: "Required Platform approvals must be unique.",
      })
    }
    const issuedAt = Date.parse(envelope.issuedAt)
    const recentAt = Date.parse(envelope.recentAuthenticationAt)
    if (recentAt > issuedAt || issuedAt - recentAt > 15 * 60 * 1000) {
      context.addIssue({
        code: "custom",
        path: ["recentAuthenticationAt"],
        message: "Platform recent authentication is outside the bounded window.",
      })
    }
  })

export const platformCommandSafeSummaryV1Schema = z
  .object({
    targetType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    targetId: entityIdSchema,
    state: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/),
    version: z.number().int().nonnegative().safe(),
  })
  .strict()

const platformCommandSuccessV1Schema = z
  .object({
    responseVersion: z.literal(1),
    ok: z.literal(true),
    platformCommandId: platformCommandIdSchema,
    correlationId: correlationIdSchema,
    result: platformCommandSafeSummaryV1Schema,
  })
  .strict()

const platformCommandFailureV1Schema = z
  .object({
    responseVersion: z.literal(1),
    ok: z.literal(false),
    platformCommandId: platformCommandIdSchema.nullable(),
    correlationId: correlationIdSchema.nullable(),
    error: z
      .object({
        code: z.enum([
          "PLATFORM_COMMAND_INVALID",
          "PLATFORM_COMMAND_DENIED",
          "PLATFORM_VERSION_CONFLICT",
          "PLATFORM_APPROVAL_INVALID",
          "PLATFORM_IDEMPOTENCY_CONFLICT",
          "PLATFORM_COMMAND_FAILED",
        ]),
        retryable: z.boolean(),
        message: z.string().min(1).max(128),
        current: platformCommandSafeSummaryV1Schema.optional(),
      })
      .strict(),
  })
  .strict()

export const platformCommandResultV1Schema = z.discriminatedUnion("ok", [
  platformCommandSuccessV1Schema,
  platformCommandFailureV1Schema,
])

export const platformAuditEventV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformAuditEventId: platformAuditEventIdSchema,
    platformCommandId: platformCommandIdSchema,
    correlationId: correlationIdSchema,
    actorPlatformOperatorId: platformOperatorIdSchema,
    action: z.string().regex(/^[a-z][a-z0-9_.-]{2,95}$/),
    targetType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    targetId: entityIdSchema,
    targetTenantId: tenantIdSchema.nullable(),
    beforeVersion: z.number().int().nonnegative().safe(),
    afterVersion: z.number().int().positive().safe(),
    summary: boundedRecord(4_096),
    retentionClass: z.enum(["permission_7y", "operational_4y", "security_2y"]),
    occurredAt: timestampSchema,
  })
  .strict()
  .refine((event) => event.afterVersion > event.beforeVersion, {
    message: "Platform audit event must advance the target version.",
  })

export const platformOutboxEventV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformOutboxEventId: platformOutboxEventIdSchema,
    platformCommandId: platformCommandIdSchema,
    correlationId: correlationIdSchema,
    eventType: z.enum([
      "PlatformOperatorChanged",
      "PlatformRoleChanged",
      "TenantLifecycleChanged",
      "PlatformAuditRecorded",
      "TenantProvisioningChanged",
      "TenantServiceLifecycleChanged",
      "PlatformEntitlementChanged",
      "PlatformBillingReconciled",
      "ProviderIntegrationChanged",
      "FleetHealthObserved",
      "TenantRecoveryChanged",
      "ReleaseCohortChanged",
      "PlatformSupportCaseChanged",
      "SupportAccessGrantChanged",
      "SupportSessionChanged",
      "SupportBreakGlassActivated",
      "PlatformCommunicationChanged",
      "PlatformCommunicationEscalated",
      "PlatformIncidentChanged",
      "SecurityReviewChanged",
      "PlatformLegalHoldChanged",
      "PlatformAnalyticsExportRequested",
    ]),
    eventVersion: z.literal(1),
    aggregateType: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    aggregateId: entityIdSchema,
    aggregateVersion: z.number().int().positive().safe(),
    targetTenantId: tenantIdSchema.nullable(),
    payload: boundedRecord(8_192),
    occurredAt: timestampSchema,
  })
  .strict()

export type PlatformAuditEventV1 = z.infer<typeof platformAuditEventV1Schema>
export type PlatformCommandEnvelopeV1 = z.infer<
  typeof platformCommandEnvelopeV1Schema
>
export type PlatformCommandResultV1 = z.infer<typeof platformCommandResultV1Schema>
export type PlatformOutboxEventV1 = z.infer<typeof platformOutboxEventV1Schema>
